//
//  helpTours.ts
//
//  Copyright:
//    NewtonGreen Technologies Pty. Ltd.
//    Level 4, 175 Scott Street
//    Newcastle, NSW, 2300
//    Australia
//
//    E-mail: support@newtongreen.com
//    Tel: (02) 4925 5288
//
//    All Rights Reserved.
//
//  Function of this module:
//    This module defines the hard-coded onboarding tours offered from the help panel.
//

import { helpStep, helpTourTargets, pageTitleStep, type HelpTourStep } from "./helpTourTargets";
import pageTours from "./pageTours";

export {
    helpStep,
    helpTourTargets,
    pageTarget,
    pageTitleStep,
    type HelpTourStep
} from "./helpTourTargets";

/**
 * The tour offered on every page without a page tour.
 *
 * Postconditions: Steps are in reading order over the shared shell targets. The provider drops the
 * steps whose target a page does not display, so the same list serves list, form and lookup pages.
 */
export const defaultHelpTour: readonly HelpTourStep[] = [
    pageTitleStep("This page", "The heading names the page, or the record, you are working with."),
    {
        id: helpTourTargets.breadcrumbs,
        title: "Where you are",
        content:
            "The breadcrumb trail shows how you reached this page. Select any level to go back to it."
    },
    {
        id: helpTourTargets.navigation,
        title: "Sections",
        content:
            "Move between the related sections of this record here. Collapse the side menu from the button at the top left."
    },
    {
        id: helpTourTargets.gridFilter,
        title: "Find records",
        content:
            "Type to filter the list. Use the switch to include inactive records, which are otherwise hidden."
    },
    {
        id: helpTourTargets.gridActions,
        title: "Actions",
        content:
            "Create new records or run the actions available for this list. Select a row in the list to open that record."
    },
    {
        id: helpTourTargets.formActions,
        title: "Saving",
        content:
            "Save your changes here. Fields with problems are highlighted, and the page will not save until required fields are complete."
    },
    helpStep
];

/**
 * Page tours keyed by route pattern (see getRoutePattern). A route that shares its help with
 * another route (see helpPages) also shares that route's tour. Tours are code-owned: they point at
 * rendered elements, so they change with the pages they describe.
 */
const helpTours: Readonly<Record<string, readonly HelpTourStep[]>> = pageTours;

export default helpTours;
